import { openWorkOrderFor } from './plan-view.js';
import type { PostponementStatus, PostponementView, WorkOrderView } from './types.js';

/**
 * Ertelemeler ekranının saf görünüm kuralları (ADR 0108 §4): özet sayıları, süzgeç, arama ve
 * sıralama. İki uygulamada aynı.
 */

/** Özet kartlarının süzgeci: bekleyen, yürürlükte, ofisin müdahale ettiği. */
export const POSTPONEMENT_VIEWS = ['all', 'pending', 'active', 'office'] as const;
export type PostponementViewFilter = (typeof POSTPONEMENT_VIEWS)[number];

export const POSTPONEMENT_STATUSES: readonly PostponementStatus[] = [
  'pending',
  'approved',
  'returned',
  'revoked',
];

/** Ofis ertelemeye karar verdi mi (geri çevirme ya da yeni vade; sonucu ne olursa olsun). */
export const officeIntervened = (item: PostponementView): boolean =>
  item.office.some((entry) => entry.kind !== 'comment');

export interface PostponementSummary {
  all: number;
  pending: number;
  active: number;
  office: number;
}

export function postponementSummary(items: readonly PostponementView[]): PostponementSummary {
  return {
    all: items.length,
    pending: items.filter((i) => i.status === 'pending').length,
    active: items.filter((i) => i.active).length,
    office: items.filter((i) => i.office.length > 0).length,
  };
}

export interface PostponementFilter {
  view: PostponementViewFilter;
  status: PostponementStatus | '';
  search: string;
}

export function filterPostponements(
  items: readonly PostponementView[],
  filter: PostponementFilter,
): PostponementView[] {
  const needle = filter.search.trim().toLocaleLowerCase();
  return items.filter((item) => {
    if (filter.view === 'pending' && item.status !== 'pending') return false;
    if (filter.view === 'active' && !item.active) return false;
    if (filter.view === 'office' && item.office.length === 0) return false;
    if (filter.status !== '' && item.status !== filter.status) return false;
    if (needle === '') return true;
    return [
      item.jobCode,
      item.jobTitle,
      item.equipmentCode,
      item.equipmentName,
      item.reason,
      item.vesselName,
      item.requestedByName,
      item.approvedByName,
    ]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLocaleLowerCase().includes(needle));
  });
}

export type PostponementSortKey = 'requestedAt' | 'newDue' | 'delay' | 'status';

const STATUS_ORDER: Record<PostponementStatus, number> = {
  pending: 0,
  approved: 1,
  revoked: 2,
  returned: 3,
};

export function sortPostponements(
  items: readonly PostponementView[],
  sort: { key: PostponementSortKey; dir: 'asc' | 'desc' },
): PostponementView[] {
  const value = (item: PostponementView): string | number => {
    if (sort.key === 'newDue') return item.newDueOn ?? '';
    if (sort.key === 'delay') return item.delayDays ?? item.delayHours ?? -1;
    if (sort.key === 'status') return STATUS_ORDER[item.status];
    return item.requestedAt;
  };
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return order * sign || b.requestedAt.localeCompare(a.requestedAt) || a.id.localeCompare(b.id);
  });
}

export type WorkOrderShortcut = { kind: 'open'; workOrder: WorkOrderView } | { kind: 'create' };

/**
 * Ertelemeler satırından iş emri kısayolu (ortak 2026-10-25; ADR 0108 Ek): işin açık iş emri
 * varsa ona götürür (yalnız görme yeter); yoksa iş emri açma yetkisi olana "Create work order".
 * Maintenance Plan'daki akışın aynısıdır (aynı uç, aynı yetki `work_orders.create`). Ertelemenin
 * durumuna bakılmaz: iş emri açmak ertelemeden bağımsızdır (bekleyen, iade edilen ve geri
 * çevrilen ertelemede de). İş emri listesi okunamadıysa (`null`) kısayol yok.
 */
export function workOrderShortcut(
  item: Pick<PostponementView, 'jobId'>,
  workOrders: readonly WorkOrderView[] | null,
  canCreate: boolean,
): WorkOrderShortcut | null {
  if (workOrders === null) return null;
  const open = openWorkOrderFor(workOrders, item.jobId);
  if (open) return { kind: 'open', workOrder: open };
  return canCreate ? { kind: 'create' } : null;
}
